import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import {
  FilterPopover,
  FilterPopoverRow,
} from 'src/components/atlas/molecules/FilterPopover/FilterPopover';
import * as styles from 'src/components/atlas/molecules/FilterPopover/FilterPopover.styles';
import {
  CustomFieldType,
  CustomFieldValueType,
  ReportStatus,
} from 'src/graphql';
import { CaseSearchOptionValues } from './helpers/searchTableHelper';

export type SpeakUpFilterColumn =
  | 'Case.id'
  | 'Case.name'
  | 'CaseLead.name'
  | 'Categories.locales'
  | 'Case.status'
  | 'CaseHasUsers.User.name'
  | 'CaseTags.name'
  | 'CaseTags.TagType.name'
  | 'CustomField.value';

export type SpeakUpFilterValue = Partial<Record<SpeakUpFilterColumn, string>>;

export type SpeakUpFilterRow = FilterPopoverRow<SpeakUpFilterColumn>;

const BASE_COLUMN_OPTIONS: { value: SpeakUpFilterColumn; label: string }[] = [
  {
    value: CaseSearchOptionValues.CASE_ID as SpeakUpFilterColumn,
    label: 'Case ID',
  },
  {
    value: CaseSearchOptionValues.CASE_NAME as SpeakUpFilterColumn,
    label: 'Case Name',
  },
  {
    value: CaseSearchOptionValues.CASE_LEAD as SpeakUpFilterColumn,
    label: 'Case Lead',
  },
  {
    value: CaseSearchOptionValues.CASE_CATEGORY as SpeakUpFilterColumn,
    label: 'Category',
  },
  {
    value: CaseSearchOptionValues.CASE_STATUS as SpeakUpFilterColumn,
    label: 'Case Status',
  },
  {
    value: CaseSearchOptionValues.REPORT_USER_NAME as SpeakUpFilterColumn,
    label: 'Case Team Members',
  },
  {
    value: CaseSearchOptionValues.CASE_TAG_NAME as SpeakUpFilterColumn,
    label: 'Case Tag',
  },
  {
    value: CaseSearchOptionValues.CASE_TAG_TYPE as SpeakUpFilterColumn,
    label: 'Case Tag Type',
  },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: ReportStatus.New, label: 'New' },
  { value: ReportStatus.Read, label: 'Read' },
  { value: ReportStatus.Investigating, label: 'Investigating' },
  { value: ReportStatus.Closed, label: 'Closed' },
  { value: ReportStatus.Draft, label: 'Draft' },
];

const DROPDOWN_COLUMNS: Partial<
  Record<SpeakUpFilterColumn, { value: string; label: string }[]>
> = {
  [CaseSearchOptionValues.CASE_STATUS as SpeakUpFilterColumn]: STATUS_OPTIONS,
};

export interface SpeakUpFilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  rows: SpeakUpFilterRow[];
  onRowsChange: (rows: SpeakUpFilterRow[]) => void;
  value: SpeakUpFilterValue;
  onChange: (value: SpeakUpFilterValue) => void;
  onApply: (value: SpeakUpFilterValue, rows: SpeakUpFilterRow[]) => void;
  onRemoveAll: () => void;
  makeId: () => string;
  customFieldsEnabled?: boolean;
  customFields?: Pick<
    CustomFieldType,
    'id' | 'label' | 'fieldType' | 'optionDetails'
  >[];
}

export const SpeakUpFilterPopover: React.FC<SpeakUpFilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  rows,
  onRowsChange,
  value,
  onChange,
  onApply,
  onRemoveAll,
  makeId,

  customFieldsEnabled,
  customFields = [],
}) => {
  const columnOptions = useMemo(() => {
    if (customFieldsEnabled) {
      return [
        ...BASE_COLUMN_OPTIONS,
        {
          value: 'CustomField.value' as SpeakUpFilterColumn,
          label: 'Custom Field',
        },
      ];
    }
    return BASE_COLUMN_OPTIONS;
  }, [customFieldsEnabled]);

  const handleValueChange = (column: SpeakUpFilterColumn, text: string) => {
    if (text) {
      onChange({ ...value, [column]: text });
    } else {
      const next = { ...value };
      delete next[column];
      onChange(next);
    }
  };

  const clearColumnValue = (
    column: SpeakUpFilterColumn,
    v: SpeakUpFilterValue,
  ): SpeakUpFilterValue => {
    const next = { ...v };
    delete next[column];
    return next;
  };

  const renderValueControl = (row: SpeakUpFilterRow) => {
    if (row.column === 'CustomField.value') {
      const encoded = value[row.column as SpeakUpFilterColumn] ?? '';
      const fieldId = encoded.split(':')[0];
      const fieldValue = encoded.split(':').slice(1).join(':');
      const selectedField = customFields.find((f) => f.id === fieldId);
      const isSingleSelect =
        selectedField?.fieldType === CustomFieldValueType.SingleSelect;
      const activeOptions = (selectedField?.optionDetails ?? [])
        .filter((o) => o.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder);

      return (
        <>
          <FormControl size="small" sx={styles.field}>
            <InputLabel shrink>Field</InputLabel>
            <Select
              displayEmpty
              label="Field"
              value={fieldId}
              onChange={(e) => {
                handleValueChange(
                  row.column as SpeakUpFilterColumn,
                  e.target.value ? `${e.target.value}:` : '',
                );
              }}
              inputProps={{ 'data-testid': 'speak-up-filter-custom-field-id' }}
              renderValue={(selected) =>
                selected ? (
                  (customFields.find((f) => f.id === selected)?.label ??
                  selected)
                ) : (
                  <Typography component="span" sx={styles.placeholderText}>
                    Select a field
                  </Typography>
                )
              }
            >
              {customFields.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isSingleSelect ? (
            <FormControl size="small" sx={styles.field}>
              <InputLabel shrink>Value</InputLabel>
              <Select
                displayEmpty
                label="Value"
                disabled={!fieldId}
                value={fieldValue}
                onChange={(e) => {
                  handleValueChange(
                    row.column as SpeakUpFilterColumn,
                    fieldId ? `${fieldId}:${e.target.value}` : '',
                  );
                }}
                SelectDisplayProps={
                  {
                    'data-testid': 'speak-up-filter-custom-field-text',
                  } as React.HTMLAttributes<HTMLDivElement>
                }
                renderValue={(selected) =>
                  selected ? (
                    (activeOptions.find((o) => o.id === selected)?.label ??
                    selected)
                  ) : (
                    <Typography component="span" sx={styles.placeholderText}>
                      Select a value
                    </Typography>
                  )
                }
              >
                {activeOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              label="Value"
              placeholder="Filter value"
              disabled={!fieldId}
              value={fieldValue}
              onChange={(e) => {
                handleValueChange(
                  row.column as SpeakUpFilterColumn,
                  fieldId ? `${fieldId}:${e.target.value}` : '',
                );
              }}
              sx={styles.field}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                'data-testid': 'speak-up-filter-custom-field-text',
              }}
            />
          )}
        </>
      );
    }

    if (row.column && DROPDOWN_COLUMNS[row.column as SpeakUpFilterColumn]) {
      const options = DROPDOWN_COLUMNS[row.column as SpeakUpFilterColumn]!;
      return (
        <FormControl size="small" sx={styles.field}>
          <InputLabel shrink>Value</InputLabel>
          <Select
            displayEmpty
            label="Value"
            value={value[row.column as SpeakUpFilterColumn] ?? ''}
            onChange={(e) =>
              handleValueChange(
                row.column as SpeakUpFilterColumn,
                e.target.value,
              )
            }
            inputProps={{
              'data-testid': `speak-up-filter-value-${row.column}`,
            }}
            renderValue={(selected) =>
              selected ? (
                (options.find((o) => o.value === selected)?.label ?? selected)
              ) : (
                <Typography component="span" sx={styles.placeholderText}>
                  Select a value
                </Typography>
              )
            }
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        size="small"
        label="Value"
        placeholder="Filter value"
        disabled={!row.column}
        value={
          row.column ? (value[row.column as SpeakUpFilterColumn] ?? '') : ''
        }
        onChange={(e) =>
          row.column &&
          handleValueChange(row.column as SpeakUpFilterColumn, e.target.value)
        }
        sx={styles.field}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          'data-testid': `speak-up-filter-value-${row.column || 'empty'}`,
        }}
      />
    );
  };

  return (
    <FilterPopover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      rows={rows}
      onRowsChange={onRowsChange}
      value={value}
      onChange={onChange}
      onApply={(value, ref) => onApply(value, ref)}
      onRemoveAll={onRemoveAll}
      makeId={makeId}
      columnOptions={columnOptions}
      clearColumnValue={clearColumnValue}
      renderValueControl={renderValueControl}
      testIdPrefix="speak-up"
    />
  );
};
